import { useSelector } from 'react-redux';
import { InfoCircle24, Stack24, Percent24, PictureTshirt24 } from '@vinted/monochrome-icons';
import { Text, Modal, Button, Navigation, Cell, Image, Divider, Icon, ColorCode, Spacer } from '@vinted/web-ui';
import useTracking from 'hooks/useTracking';
import useTranslate from 'hooks/useTranslate';
import useIsDarkMode from 'hooks/useIsDarkMode';
import ContentLoader from 'components/ContentLoader';
import ScrollableArea from 'components/ScrollableArea';
import FaqEntryUrl from 'components/FaqEntryUrl';
import { formatCurrencyAmount } from 'libs/utils/formatString';
import { navigateToPage } from 'libs/utils/window';
import { clickEvent } from 'libs/common/event-tracker/events';
import { getLocale } from 'state/intl/selectors';
import { FEATURED_COLLECTION_URL } from 'constants/routes';
import { FaqEntryType } from 'constants/faq-entry';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { Screen } from 'constants/tracking/screens';
import { getAssetPath } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const MultipleCollectionsOnboardingModal = _ref => {
  let {
    isShown,
    isLoading,
    cheapestPlan,
    maxActiveCollectionsCount,
    onClose,
    onPriceInfoClick
  } = _ref;
  const {
    track
  } = useTracking();
  const translate = useTranslate('featured_collection.multiple.onboarding_modal');
  const isDarkMode = useIsDarkMode();
  const locale = useSelector(getLocale);

  function handleCreateCollectionsClick() {
    track(clickEvent({
      screen: Screen.FeaturedCollection,
      target: ClickableElement.CreateCollection
    }));
    navigateToPage(FEATURED_COLLECTION_URL);
  }

  function renderNavigation() {
    return /*#__PURE__*/_jsx(Navigation, {
      right: /*#__PURE__*/_jsx(Button, {
        deprecatedIconName: "x",
        theme: "amplified",
        styling: Button.Styling.Flat,
        testId: "multiple-collections-onboarding-close-button",
        onClick: onClose
      }),
      body: /*#__PURE__*/_jsx(Text, {
        type: Text.Type.Title,
        width: Text.Width.Parent,
        alignment: Text.Alignment.Center,
        text: translate('title')
      })
    });
  }

  function renderDeal() {
    if (!cheapestPlan) return null;
    const totalPrice = formatCurrencyAmount(cheapestPlan.totalPrice, locale);
    return /*#__PURE__*/_jsx("div", {
      className: "u-ui-margin-horizontal-large u-ui-margin-vertical-x-small",
      children: /*#__PURE__*/_jsx(Cell, {
        styling: Cell.Styling.Tight,
        title: translate('duration_day_count', {
          count: cheapestPlan.effectiveDays
        }, {
          count: cheapestPlan.effectiveDays
        }),
        suffix: /*#__PURE__*/_jsx(Button, {
          styling: Button.Styling.Flat,
          theme: "amplified",
          text: translate('price_from', {
            price: totalPrice
          }),
          icon: /*#__PURE__*/_jsx(Icon, {
            name: InfoCircle24,
            color: Icon.Color.GreyscaleLevel3
          }),
          iconPosition: Button.IconPosition.Right,
          onClick: onPriceInfoClick,
          testId: "multiple-collections-onboarding-price-info-button"
        })
      })
    });
  }

  function renderContent() {
    if (isLoading) {
      return /*#__PURE__*/_jsx(ContentLoader, {
        styling: ContentLoader.Styling.Wide,
        testId: "multiple-collections-onboarding-loader"
      });
    }

    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Image, {
        src: getAssetPath('featured-collection-onboarding.png', isDarkMode),
        testId: "multiple-collections-onboarding-image"
      }), /*#__PURE__*/_jsx(Cell, {
        children: /*#__PURE__*/_jsx(Text, {
          text: translate('intro.title'),
          type: Text.Type.Heading
        })
      }), /*#__PURE__*/_jsx(Cell, {
        title: translate('intro.groups.title'),
        body: translate('intro.groups.body', {
          count: maxActiveCollectionsCount
        }, {
          count: maxActiveCollectionsCount
        }),
        prefix: /*#__PURE__*/_jsx(Icon, {
          name: Stack24,
          color: ColorCode.GreyscaleLevel3
        })
      }), /*#__PURE__*/_jsx(Cell, {
        title: translate('intro.sale.title'),
        body: translate('intro.sale.body'),
        prefix: /*#__PURE__*/_jsx(Icon, {
          name: Percent24,
          color: ColorCode.GreyscaleLevel3
        })
      }), /*#__PURE__*/_jsx(Cell, {
        title: translate('intro.feature.title'),
        body: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Text, {
            children: translate('intro.feature.body')
          }), /*#__PURE__*/_jsx(Spacer, {
            size: Spacer.Size.X2Large
          }), /*#__PURE__*/_jsx(FaqEntryUrl, {
            type: FaqEntryType.Collection,
            render: url => /*#__PURE__*/_jsx("a", {
              href: url,
              target: "_blank",
              rel: "noreferrer",
              children: translate('actions.learn_more')
            })
          }), /*#__PURE__*/_jsx(Spacer, {
            size: Spacer.Size.Large
          })]
        }),
        prefix: /*#__PURE__*/_jsx(Icon, {
          name: PictureTshirt24,
          color: ColorCode.GreyscaleLevel3
        })
      })]
    });
  }

  return /*#__PURE__*/_jsx(Modal, {
    show: isShown,
    hasScrollableContent: true,
    testId: "multiple-collections-onboarding-modal",
    children: /*#__PURE__*/_jsxs("div", {
      className: "u-fill-width",
      children: [renderNavigation(), /*#__PURE__*/_jsx(ScrollableArea, {
        children: renderContent()
      }), /*#__PURE__*/_jsx(Divider, {}), renderDeal(), /*#__PURE__*/_jsx("div", {
        className: "u-ui-margin-horizontal-large u-ui-margin-bottom-large",
        children: /*#__PURE__*/_jsx(Button, {
          text: translate('actions.create'),
          styling: Button.Styling.Filled,
          onClick: handleCreateCollectionsClick,
          testId: "multiple-collections-onboarding-create-button"
        })
      })]
    })
  });
};

export default MultipleCollectionsOnboardingModal;